/**
 * The inventory, and the two numbers it decides.
 *
 * Editable — unlike the rest of the sheet, which is read-only because the
 * engine owns it. What you are carrying is genuinely the player's; **what it
 * produces is not**. Armour class and encumbrance are re-derived server-side on
 * every write and come back with the sheet, so nothing here computes either.
 *
 * The AC breakdown is shown itemised rather than as one number, because on a
 * **descending** scale a change reads backwards: picking up a shield makes the
 * number go *down*, and a player who cannot see which line moved has no way to
 * tell that from a bug.
 */

import { useState } from 'react';

import { optionLabel } from './options.js';

export interface InventoryItem {
  readonly name: string;
  readonly quantity: number;
  readonly equipped?: boolean;
  readonly notes?: string;
  /**
   * The world-clock round this was lit, if it is a light source and has
   * been. Engine-written — this section never sends it back on an ordinary
   * edit, only reads it for display, since `onChange` writes a whole new
   * array and `litAtGameTime` is stamped through a separate action
   * (`onLight`) precisely so an ordinary edit here can never clear it.
   */
  readonly litAtGameTime?: number;
  /** A gem or art object's printed worth in gold — engine-written, read-only here. */
  readonly valueGp?: number;
}

/**
 * Exactly the fields a player edits — never `litAtGameTime`.
 *
 * **A pure function, exported and separate from any effect**, the same
 * `declaringFor` shape: `InventorySection`'s own edit handlers (equip, drop)
 * spread an existing entry through untouched, so a lit item's
 * `litAtGameTime` — fetched from the server moments ago — would otherwise
 * ride along on the very next unrelated edit and be hard-refused by the
 * server, turning "toggle a checkbox" into "your torch refuses to unequip."
 * **Allowlisted, not blocklisted**: writing out exactly the fields a player
 * may edit, rather than naming the one engine-owned field to strip, is what
 * keeps the next engine-only field from reopening this the same way.
 */
export function toWritableInventory(items: readonly InventoryItem[]): readonly InventoryItem[] {
  return items.map(({ name, quantity, equipped, notes }) => ({
    name,
    quantity,
    ...(equipped !== undefined ? { equipped } : {}),
    ...(notes !== undefined ? { notes } : {}),
  }));
}

/**
 * A loose, **courtesy-only** guess at whether this reads as a light source —
 * "torch", "lantern", "candle" in the name. Purely for deciding whether to
 * show the light button at all; the server owns the real answer
 * (`engine/light.ts` → `classifyLightSource`) and refuses anything this
 * guessed wrong, the same "the client is a courtesy" footing the verb bar
 * already stands on.
 */
function looksLikeLightSource(name: string): boolean {
  const lower = name.toLowerCase();
  return ['torch', 'lantern', 'candle'].some((word) => lower.includes(word));
}

export interface ArmourClassBreakdown {
  readonly armourClass: number;
  readonly base: number;
  readonly armour?: string;
  readonly shieldBonus: number;
  readonly helmetPenalty: number;
  readonly dexterityModifier: number;
  readonly shieldLimit?: string;
}

export interface Load {
  readonly weight: number;
  readonly stage: string;
  readonly movement: number;
}

export interface InventorySectionProps {
  readonly items: readonly InventoryItem[];
  readonly armourClass?: ArmourClassBreakdown;
  readonly load?: Load;
  /** Writes the whole list. The server re-derives everything from it. */
  readonly onChange: (items: readonly InventoryItem[]) => void;
  /**
   * Lights a light source, by name — a separate action from `onChange`
   * because it stamps `litAtGameTime` from the table's own clock server-side
   * and must never travel through a whole-array inventory edit, which would
   * have no way to tell "the client doesn't know about this field yet" apart
   * from "the client wants it cleared."
   */
  readonly onLight?: (name: string) => void;
  /** Puts a lit light out, by name; its own request for the same reason `onLight` is. */
  readonly onPutOut?: (name: string) => void;
  /**
   * Which carried lights are burning, and which have burned out, by item name. **From the server**, which has the
   * table's clock: this sheet used to say "lit" for anything that had ever been lit, burned out or not.
   */
  readonly lights?: Readonly<
    Record<string, { readonly lit: boolean; readonly spent: boolean; readonly minutesRemaining?: number }>
  >;
  /** Each item's name as a person reads it, by the key it is stored under (the server's `itemNames`). */
  readonly names?: Readonly<Record<string, string>>;
  /** The server's reason an edit was refused, shown beside the list it concerns. */
  readonly notice?: string;
  readonly readOnly?: boolean;
}

/**
 * The word beside a carried light: how long it has left while it burns, "burned out" once it is spent, "partly
 * burned" for one that was put out with some left, and nothing for one never lit. Falls back to the old
 * "lit" for a server that does not send `lights`.
 */
export function lightTag(
  entry: { readonly litAtGameTime?: number },
  light: { readonly lit: boolean; readonly spent: boolean; readonly minutesRemaining?: number } | undefined,
): string | undefined {
  if (light === undefined) return entry.litAtGameTime !== undefined ? 'lit' : undefined;
  if (light.spent) return 'burned out';
  const left = light.minutesRemaining;
  const span = left === undefined ? '' : left >= 60 ? ` · ${Math.floor(left / 60)} h ${Math.round(left % 60)} min left` : ` · ${Math.round(left)} min left`;
  if (light.lit) return `lit${span}`;
  return left !== undefined ? `put out${span}` : undefined;
}

export function InventorySection({
  items,
  armourClass,
  load,
  onChange,
  onLight,
  onPutOut,
  lights,
  names,
  notice,
  readOnly,
}: InventorySectionProps) {
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('1');

  const add = () => {
    const trimmed = name.trim();
    if (trimmed === '') return;
    const count = Number(quantity);
    onChange([
      ...items,
      { name: trimmed, quantity: Number.isFinite(count) && count > 0 ? count : 1 },
    ]);
    setName('');
    setQuantity('1');
  };

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Inventory</h2>

      {armourClass && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {/* Every component is a signed delta on a base the armour *sets*.
              Spelling the arithmetic out is what makes a descending scale
              legible: −1 from a shield is an improvement. */}
          AC {armourClass.armourClass} = {armourClass.base}
          {armourClass.armour ? ` (${optionLabel(armourClass.armour)})` : ' (unarmoured)'}
          {armourClass.shieldBonus !== 0 && ` ${signed(armourClass.shieldBonus)} shield`}
          {armourClass.helmetPenalty !== 0 &&
            ` ${signed(armourClass.helmetPenalty)} no helmet`}
          {armourClass.dexterityModifier !== 0 &&
            ` ${signed(armourClass.dexterityModifier)} Dex`}
          {armourClass.shieldLimit && ` · shield answers ${armourClass.shieldLimit}`}
        </p>
      )}
      {load && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          carrying {load.weight} lb · {optionLabel(load.stage)} · movement {load.movement}
        </p>
      )}

      <ul className="space-y-0.5 text-sm">
        {items.map((entry, index) => (
          <li key={`${entry.name}-${index}`} className="flex items-baseline gap-2">
            <label className="flex flex-1 items-baseline gap-2">
              <input
                type="checkbox"
                checked={entry.equipped === true}
                disabled={readOnly}
                aria-label={`Equip ${entry.name}`}
                onChange={(e) =>
                  onChange(
                    items.map((other, i) =>
                      i === index ? { ...other, equipped: e.target.checked } : other,
                    ),
                  )
                }
              />
              <span>
                {names?.[entry.name] ?? optionLabel(entry.name)}
                {entry.quantity > 1 && (
                  <span className="text-xs text-neutral-500 dark:text-neutral-400"> ×{entry.quantity}</span>
                )}
                {entry.valueGp !== undefined && (
                  <span className="ml-1 text-xs text-neutral-500 dark:text-neutral-400">
                    worth {entry.valueGp} gp
                  </span>
                )}
                {lightTag(entry, lights?.[entry.name]) && (
                  <>
                    {' '}
                    <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                      {lightTag(entry, lights?.[entry.name])}
                    </span>
                  </>
                )}
              </span>
            </label>
            {!readOnly && looksLikeLightSource(entry.name) && (
              // **What the button does follows what the light is doing.** It used to say "light" for a lantern
              // that was already lit, and there was no way to put one out at all.
              lights?.[entry.name]?.lit ?? entry.litAtGameTime !== undefined ? (
                onPutOut && (
                  <button
                    type="button"
                    aria-label={`Put out ${entry.name}`}
                    onClick={() => onPutOut(entry.name)}
                    className="text-xs text-amber-600 dark:text-amber-400"
                  >
                    put out
                  </button>
                )
              ) : (
                onLight &&
                !lights?.[entry.name]?.spent && (
                  <button
                    type="button"
                    aria-label={`Light ${entry.name}`}
                    onClick={() => onLight(entry.name)}
                    className="text-xs text-amber-600 dark:text-amber-400"
                  >
                    light
                  </button>
                )
              )
            )}
            {!readOnly && (
              <button
                type="button"
                aria-label={`Drop ${entry.name}`}
                onClick={() => onChange(items.filter((_, i) => i !== index))}
                className="text-xs text-neutral-500 dark:text-neutral-400"
              >
                drop
              </button>
            )}
          </li>
        ))}
        {items.length === 0 && <li className="text-xs text-neutral-500 dark:text-neutral-400">carrying nothing</li>}
      </ul>

      {notice && (
        <p role="alert" id="inventory-notice" className="text-sm text-amber-800 dark:text-amber-300">
          {notice}
        </p>
      )}

      {!readOnly && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <label className="sr-only" htmlFor="item-name">
            Item
          </label>
          {/* Free text for what no price list names — a keepsake, a letter, a
              note to self. **Gear is bought** from a shopkeeper at the table, and the server
              refuses a priced name here with the way out, shown above. */}
          <input
            id="item-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="keepsake or note (gear: buy it from a shopkeeper)"
            className="w-44 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
          <label className="sr-only" htmlFor="item-quantity">
            Quantity
          </label>
          <input
            id="item-quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            inputMode="numeric"
            className="w-16 rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
          />
          <button
            type="submit"
            className="rounded-md border border-neutral-300 px-3 py-1 text-sm dark:border-neutral-700"
          >
            Add
          </button>
        </form>
      )}
    </section>
  );
}

/** `+1` / `−1`, so a delta never reads as a total. */
function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}
